import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import addNotoSansFont from './noto-sans'; // Adjust path as needed

const exportService = {
  exportToXLS(data, headers, fileName = 'report.xlsx', sheetName = 'Report') {
    const formattedData = data.map((item) =>
      headers.reduce((acc, header) => {
        const key = header.key || header;
        acc[header.label || key] = key === 'date' || key === 'check_in' || key === 'check_out'
          ? moment(item[key]).format('YYYY-MM-DD')
          : item[key];
        return acc;
      }, {})
    );

    const ws = XLSX.utils.json_to_sheet(formattedData, {
      header: headers.map((h) => h.label || h.key || h),
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    ws['!cols'] = headers.map(() => ({ wch: 20 }));
    XLSX.writeFile(wb, fileName);
  },

  exportToCSV(data, headers, fileName = 'report.csv') {
    const headerRow = headers.map((h) => h.label || h.key || h).join(',');
    const rows = data.map((item) =>
      headers
        .map((header) => {
          const key = header.key || header;
          const value = key === 'date' || key === 'check_in' || key === 'check_out'
            ? moment(item[key]).format('YYYY-MM-DD')
            : item[key];
          return `"${value || ''}"`;
        })
        .join(',')
    );

    const csvContent = [headerRow, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  exportToPDF(data, headers, title, fileName, returnBlob = false, landscape = false, totals = [], exportFooter = []) {
    const doc = new jsPDF({
      orientation: landscape ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: false // Disable compression for better quality
    });

    let fontLoaded = false;
    try {
      // Register Noto Sans font
      addNotoSansFont(doc);
      doc.setFont('NotoSans', 'normal');
      fontLoaded = true;
    } catch (e) {
      console.error('Failed to load NotoSans font:', e);
      doc.setFont('helvetica', 'normal'); // Fallback to helvetica
    }

    // Validate inputs
    if (!data || !Array.isArray(data) || data.length === 0) {
      doc.setFontSize(12);
      doc.text("No data available to export.", 14, 20);
      if (returnBlob) {
        return doc.output('blob');
      }
      doc.save(fileName);
      return;
    }

    if (!headers || !Array.isArray(headers) || headers.length === 0) {
      doc.setFontSize(12);
      doc.text("No headers provided for export.", 14, 20);
      if (returnBlob) {
        return doc.output('blob');
      }
      doc.save(fileName);
      return;
    }

    try {
      // Header Layout
      const pageWidth = landscape ? 297 : 210;
      const margin = 5;

      // Report Title (top center)
      let titleY = 10; // Start at 5mm from top
      doc.setFontSize(16);
      doc.setFont(fontLoaded ? 'NotoSans' : 'helvetica', 'normal');
      doc.text(title, pageWidth / 2, titleY, { align: 'center' });

      // Date (below title, centered)
      const dateText = "25th April 2026"; // Hardcoded as per image; replace with dynamic date if needed
      titleY += 5; // Space below title
      doc.setFontSize(12);
      doc.text(dateText, pageWidth / 2, titleY, { align: 'center' });

      // Property Details (top right)
      const propertyDetailsX = pageWidth - margin; // Right edge with margin
      let propertyDetailsY = 10; // Start at top, aligned with title
      doc.setFontSize(10);
      doc.setFont(fontLoaded ? 'NotoSans' : 'helvetica', 'bold');
      doc.text("Property Name", propertyDetailsX, propertyDetailsY, { align: 'right' });

      doc.setFontSize(8);
      doc.setFont(fontLoaded ? 'NotoSans' : 'helvetica', 'normal');
      propertyDetailsY += 5; // Line spacing
      doc.text("Address1", propertyDetailsX, propertyDetailsY, { align: 'right' });
      propertyDetailsY += 4;
      doc.text("Address2", propertyDetailsX, propertyDetailsY, { align: 'right' });
      propertyDetailsY += 4;
      doc.text("Phone", propertyDetailsX, propertyDetailsY, { align: 'right' });

      // Calculate header height for table positioning
      const headerHeight = Math.max(titleY, propertyDetailsY);

      // Define column styles with adjusted widths (caller can override if needed)
      const columnStyles = {};

      // Prepare table headers and data
      const headerRow = headers.map((h) => h.label || h.key || h);
      const rows = data.map((item) =>
        headers.map((header) => {
          const key = header.key || header;
          // Generic date formatting for any date field (caller can specify date keys if needed)
          if (key.toLowerCase().includes('date') && item[key]) {
            return moment(item[key]).format('YYYY-MM-DD');
          }
          return item[key] || '';
        })
      );

      // Prepare footer row for table if exportFooter is provided
      let footerRow = [];
      if (exportFooter && Array.isArray(exportFooter) && exportFooter.length > 0) {
        footerRow = headers.map((header, index) => {
          if (index === 0) return "Total"; // First column shows "Total"
          const footerEntry = exportFooter.find(entry => entry.key === (header.key || header));
          if (footerEntry) {
            return footerEntry.currency
              ? `${footerEntry.currency}${footerEntry.value.toFixed(2)}`
              : footerEntry.value.toString();
          }
          return ""; // Empty for columns without totals
        });
      }

      doc.autoTable({
        startY: headerHeight + 10, // Start table below header
        head: [headerRow],
        body: rows,
        foot: footerRow.length > 0 ? [footerRow] : undefined, // Add footer row if exists
        theme: 'grid',
        styles: {
          fontSize: 9,
          font: fontLoaded ? 'NotoSans' : 'helvetica', // Use NotoSans if loaded, else helvetica
          cellPadding: 2,
          lineWidth: 0.01,
          lineColor: [44, 62, 80]
        },
        headStyles: {
          fillColor: [0, 123, 255],
          textColor: [255, 255, 255],
          fontStyle: 'normal',
          fontSize: 9,
          font: fontLoaded ? 'NotoSans' : 'helvetica' // Use NotoSans if loaded
        },
        footStyles: {
          fillColor: [200, 200, 200], // Light gray background for footer
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          fontSize: 9,
          font: fontLoaded ? 'NotoSans' : 'helvetica' // Use NotoSans if loaded
        },
        bodyStyles: { textColor: [0, 0, 0] },
        columnStyles: columnStyles,
        margin: { left: 5, right: 5 },
        didDrawPage: (data) => {
          // Add page footer with dynamic totals
          const pageHeight = doc.internal.pageSize.height;
          doc.setFontSize(10);
          doc.setFont(fontLoaded ? 'NotoSans' : 'helvetica', 'normal');
          doc.setTextColor(0, 0, 0);
          if (totals && Array.isArray(totals) && totals.length > 0) {
            const footerText = totals.map(item => `${item.key}: ${item.value}`).join(' | ');
            doc.text(
              footerText,
              data.settings.margin.left,
              pageHeight - 10
            );
          }
        },
      });

      if (returnBlob) {
        return doc.output('blob');
      }

      doc.save(fileName);
    } catch (e) {
      console.error('Failed to generate PDF:', e);
      doc.setFontSize(12);
      doc.text("Error generating PDF. Please try again.", 14, 20);
      if (returnBlob) {
        return doc.output('blob');
      }
      doc.save(fileName);
    }
  },

  exportInvoice(data, headers, title, fileName, returnBlob = false, landscape = false, totals = {}) {
    const doc = new jsPDF({
      orientation: landscape ? 'landscape' : 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    if (!data || !Array.isArray(data) || data.length === 0) {
      doc.setFontSize(12);
      doc.text("No data available to export.", 14, 20);
      if (returnBlob) return doc.output('blob');
      doc.save(fileName);
      return;
    }

    if (!headers || !Array.isArray(headers) || headers.length === 0) {
      doc.setFontSize(12);
      doc.text("No headers provided for export.", 14, 20);
      if (returnBlob) return doc.output('blob');
      doc.save(fileName);
      return;
    }

    // Title
    doc.setFontSize(16);
    doc.text(title, landscape ? 148.5 : 105, 15, { align: 'center' });

    // Column width config (auto sizing for better layout)
    const columnStyles = {};
    headers.forEach((_, index) => {
      columnStyles[index] = { cellWidth: 'auto' };
    });

    const headerRow = headers.map((h) => h.label || h.key || h);
    const rows = data.map((item) =>
      headers.map((header) => {
        const key = header.key || header;
        return key === 'invoice_date'
          ? moment(item[key]).format('YYYY-MM-DD')
          : item[key] || '';
      })
    );

    doc.autoTable({
      startY: 25,
      head: [headerRow],
      body: rows,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [0, 123, 255], textColor: [255, 255, 255], fontStyle: 'bold' },
      bodyStyles: { textColor: [0, 0, 0] },
      columnStyles: columnStyles,
      margin: { left: 2, right: 2 },
      didDrawPage: (data) => {
        const pageHeight = doc.internal.pageSize.height;
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(
          `Total Invoices: ${rows.length}`,
          10,
          pageHeight - 10
        );
      },
    });

    if (returnBlob) return doc.output('blob');
    doc.save(fileName);
  }
};

export default exportService;